import React, { useState } from 'react';

const MetaBudgetIncreaseQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [actionType, setActionType] = useState('status_changed');
  const [newStatus, setNewStatus] = useState(record?.status || 'pendiente');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const actionTypes = [
    {
      value: 'status_changed',
      label: '🔄 Cambiar Estado',
      description: 'Actualizar el estado del registro'
    },
    {
      value: 'comment_added',
      label: '💬 Agregar Comentario',
      description: 'Añadir una nota o comentario'
    },
    {
      value: 'completed',
      label: '✅ Marcar Completado',
      description: 'Marcar como completado y agregar comentario final'
    }
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente', color: 'text-yellow-600' },
    { value: 'en_proceso', label: '🔄 En Proceso', color: 'text-blue-600' },
    { value: 'completado', label: '✅ Completado', color: 'text-green-600' },
    { value: 'cancelado', label: '❌ Cancelado', color: 'text-red-600' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim() && actionType !== 'status_changed') {
      alert('Por favor ingresa un comentario');
      return;
    }

    setLoading(true);
    
    try {
      let actionData = {
        action_type: actionType,
        details: comment.trim(),
        user_name: currentUser?.name || currentUser?.email || 'Sistema'
      };

      // Si es cambio de estado o completado, incluir el nuevo estado
      if (actionType === 'status_changed' || actionType === 'completed') {
        const finalStatus = actionType === 'completed' ? 'completado' : newStatus;
        actionData.new_status = finalStatus;
        actionData.previous_value = record.status;
        actionData.new_value = finalStatus;
        actionData.field_changed = 'status';
        
        if (actionType === 'completed' && !comment.trim()) {
          actionData.details = 'Registro marcado como completado';
        }
      }

      await onSave(record.id, actionData);
      onClose();
    } catch (error) {
      console.error('Error al guardar acción:', error);
      alert('Error al registrar la acción: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'text-gray-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="mr-3">⚡</span>
                Acción Rápida
              </h2>
              <p className="text-green-100 mt-1">
                Realizar acción sobre: {record?.nombre_cliente}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Record Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Cliente:</span>
                <div className="text-gray-900">{record?.nombre_cliente}</div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Estado Actual:</span>
                <div className={`font-medium ${getStatusColor(record?.status)}`}>
                  {statusOptions.find(opt => opt.value === record?.status)?.label || record?.status}
                </div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Campaña:</span>
                <div className="text-gray-900 truncate">{record?.campana}</div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Célula:</span>
                <div className="text-gray-900">{record?.celula}</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Action Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Tipo de Acción
              </label>
              <div className="space-y-3">
                {actionTypes.map((type) => (
                  <label key={type.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value={type.value}
                      checked={actionType === type.value}
                      onChange={(e) => setActionType(e.target.value)}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {type.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {type.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Status Selection (only for status_changed) */}
            {actionType === 'status_changed' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nuevo Estado
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Comment */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {actionType === 'comment_added' ? 'Comentario *' : 
                 actionType === 'completed' ? 'Comentario Final' : 'Comentario (Opcional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  actionType === 'status_changed' ? 'Razón del cambio de estado...' :
                  actionType === 'comment_added' ? 'Escribe tu comentario...' :
                  actionType === 'completed' ? 'Resumen final del aumento de presupuesto...' :
                  'Comentario adicional...'
                }
                rows="4"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none"
                required={actionType === 'comment_added'}
              />
              <div className="text-xs text-gray-500 mt-1">
                {actionType === 'comment_added' && 'Este campo es obligatorio'}
                {actionType === 'completed' && 'Opcional: Describe el resultado final'}
                {actionType === 'status_changed' && 'Opcional: Explica la razón del cambio'}
              </div>
            </div>

            {/* Preview */}
            {(actionType === 'status_changed' || actionType === 'completed') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-sm">
                  <span className="font-medium text-blue-800">Vista previa:</span>
                  <span className="text-blue-700">
                    El estado cambiará de 
                    <span className={`mx-1 font-medium ${getStatusColor(record?.status)}`}>
                      {statusOptions.find(opt => opt.value === record?.status)?.label}
                    </span>
                    a
                    <span className={`mx-1 font-medium ${getStatusColor(actionType === 'completed' ? 'completado' : newStatus)}`}>
                      {statusOptions.find(opt => opt.value === (actionType === 'completed' ? 'completado' : newStatus))?.label}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Procesando...
              </div>
            ) : (
              <>
                {actionType === 'status_changed' && '🔄 Cambiar Estado'}
                {actionType === 'comment_added' && '💬 Agregar Comentario'}
                {actionType === 'completed' && '✅ Marcar Completado'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaBudgetIncreaseQuickActionModal;
